import { PERMISSIONS } from "@/constants/permissions"
import { MemoryBecause } from "@/features/material-compliance/components/memory/MemoryBecause"
import { MemoryRating } from "@/features/material-compliance/components/memory/MemoryRating"
import { MemoryTrail } from "@/features/material-compliance/components/memory/MemoryTrail"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { AlertTriangle, Sparkles } from "lucide-react"
import { useEffect, useRef } from "react"

import { useRecordMemoryShown } from "../../../../../api/memoryApi"
import type { MemorySupplierHint } from "../../../../../api/types"

export interface MemorySuggestionProps {
  declId: string
  hint: MemorySupplierHint
}

/**
 * A remembered supplier mapping, offered on the gate (Turnus Memory #2).
 *
 * PURELY ADVISORY. Nothing is assigned on render, on mount, or on any click — this
 * component has no write path. It tells the reviewer what this printed name meant last
 * time; they then pick that supplier in the picker below, through the ordinary assign call.
 *
 * THE TRADE-OFF IS REAL AND IS A PRODUCT DECISION. The old "Use this" button performed the
 * assignment, which was most of the time saved by scenario #2 — reading the answer and then
 * searching for it by hand is slower than one click. What is bought instead is that memory
 * can never bind a supplier, so a remembered mapping cannot put a wrong issuer on a
 * declaration, and every assignment on record was made by a person in the picker.
 *
 * RATING IS SEPARATE FROM USING. The thumbs are about the memory, not about this
 * declaration — see `MemoryRating`. A thumbs-down is still the revert that retires a wrong
 * mapping after three, which is the only way a bad one stops being offered (scenario #7).
 *
 * CONFLICT IS CARRIED BUT NEVER ARRIVES, today. A supplier mapping reaches this gate through
 * `AliasResolver`, which drops a conflicted entry before the usage rules run, so `conflict`
 * is always empty here. The branch exists so the three surfaces are one shape and one
 * reading habit — the day the resolver hands a contested mapping back, this already asks
 * instead of being the one screen that silently drops it.
 */
export function MemorySuggestion({ declId, hint }: MemorySuggestionProps) {
  const { translate } = useTranslation()
  const recordShown = useRecordMemoryShown()

  const { entryId, supplierId } = hint
  const sides = hint.conflict ?? []
  // `> 1` not `> 0`: a flagged conflict can still arrive with one side lost upstream (tier,
  // `_not_supported_by`, the bitemporal window). Rendering that lone survivor as an ordinary
  // suggestion would be the UI picking a winner in a contradiction nobody resolved.
  const isConflict = sides.length > 1
  const renderable = isConflict || (!!entryId && !!supplierId)

  // EVERY SIDE OF A CONFLICT IS ITS OWN SIGHTING — both were put in front of somebody.
  const shownIds = isConflict
    ? sides.map((s) => s.entryId)
    : entryId
      ? [entryId]
      : []
  const sighting = shownIds.length ? `${declId}:${shownIds.join(",")}` : null

  // Reported once per (declaration, hint) — not per render, and not once per memory. The
  // same mapping shown on a second declaration is a second sighting; deduping on the entry
  // alone would silently drop it and undercount the very denominator this row exists for.
  const reported = useRef<string | null>(null)
  useEffect(() => {
    if (!renderable || !sighting || reported.current === sighting) return
    reported.current = sighting
    recordShown.mutate(
      shownIds.map((id) => ({
        entryId: id,
        surface: hint.surface,
        targetType: "declaration",
        targetId: declId,
      })),
    )
    // recordShown is a stable mutation handle; re-running on it would re-report.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderable, sighting, hint.surface, declId])

  if (!renderable) return null

  if (isConflict) {
    return (
      <div
        className="flex max-w-[360px] flex-col gap-2 rounded-md border border-attention bg-grey2 px-3 py-2 text-left"
        data-testid="memory-supplier-conflict"
      >
        <div className="flex items-start gap-2">
          <AlertTriangle
            size={13}
            className="mt-[2px] shrink-0 text-attention"
            aria-hidden="true"
            data-testid="memory-conflict-icon"
          />
          <div className="flex flex-col gap-[2px]">
            <p className="text-[11px] tracking-wide text-attention uppercase">
              {translate("MC_MEMORY_CONFLICT_TITLE")}
            </p>
            <p className="text-[11px] text-grey5">
              {translate("MC_MEMORY_CONFLICT_HINT")}
            </p>
          </div>
        </div>
        {sides.map((side) => (
          <div
            key={side.entryId}
            className="flex items-start justify-between gap-2 rounded border border-grey4 px-2 py-1.5"
            data-testid={`memory-conflict-side-${side.entryId}`}
          >
            <div className="flex min-w-0 flex-col">
              <p className="text-[13px] font-semibold text-white">
                {side.display}
              </p>
              <MemoryBecause
                origin={side.origin}
                surface="supplier"
                fallback={side.because}
                testId={side.entryId}
              />
              <MemoryTrail
                origin={side.origin}
                testId={side.entryId}
                entryId={side.entryId}
                surface={hint.surface}
              />
            </div>
            <MemoryRating
              target={{
                entryId: side.entryId,
                surface: hint.surface,
                targetType: "declaration",
                targetId: declId,
              }}
              permission={PERMISSIONS.MC_DECLARATIONS_REVIEW}
              testId={side.entryId}
              subjectLabel={side.display ?? undefined}
            />
          </div>
        ))}
      </div>
    )
  }

  return (
    <div
      className="flex max-w-[360px] flex-col gap-2 rounded-md border border-grey4 bg-grey2 px-3 py-2 text-left"
      data-testid="memory-supplier-suggestion"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-start gap-2">
          <Sparkles
            size={13}
            className="mt-[2px] shrink-0 text-attention"
            aria-hidden="true"
            data-testid="memory-icon"
          />
          <div className="flex min-w-0 flex-col gap-[2px]">
            <p className="text-[11px] tracking-wide text-grey5 uppercase">
              {translate("MC_MEMORY_SUGGESTS")}
            </p>
            <p className="text-[13px] font-semibold text-white">
              {hint.display}
            </p>
            <MemoryBecause
              origin={hint.origin}
              surface="supplier"
              fallback={hint.because}
              testId="supplier"
            />
            <MemoryTrail
              origin={hint.origin}
              testId="supplier"
              entryId={entryId}
              surface={hint.surface}
            />
          </div>
        </div>
        {entryId ? (
          <MemoryRating
            target={{
              entryId,
              surface: hint.surface,
              targetType: "declaration",
              targetId: declId,
            }}
            permission={PERMISSIONS.MC_DECLARATIONS_REVIEW}
            testId="supplier"
          />
        ) : null}
      </div>
    </div>
  )
}
